import React, { useState } from 'react';
import { ThumbsUp, ThumbsDown, Edit3, Trash2, Calendar } from 'lucide-react';
import API from '../api/axios';
import toast from 'react-hot-toast';

const PostCard = ({ post, isAdmin, groupId, onRefresh, currentUserId }) => {
  const [reacting, setReacting] = useState(false);
  const [localPost, setLocalPost] = useState(post);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: post.title, details: post.details });
  const [deleting, setDeleting] = useState(false);

  const userReaction = localPost.likes?.some(id => id === currentUserId || id?._id === currentUserId) ? 'like'
    : localPost.dislikes?.some(id => id === currentUserId || id?._id === currentUserId) ? 'dislike' : null;

  const handleReact = async (reaction) => {
    setReacting(true);
    try {
      const { data } = await API.post(`/posts/${post._id}/react`, { reaction });
      setLocalPost(prev => ({
        ...prev,
        likes: Array(data.likesCount).fill(null),
        dislikes: Array(data.dislikesCount).fill(null),
        _userReaction: data.userReaction
      }));
    } catch {
      toast.error('خطأ في التفاعل');
    } finally {
      setReacting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await API.put(`/posts/${post._id}`, editForm);
      toast.success('تم تحديث البوست ✅');
      setEditing(false);
      onRefresh();
    } catch { toast.error('فشل التحديث'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('هل تريد حذف هذا البوست؟')) return;
    setDeleting(true);
    try {
      await API.delete(`/posts/${post._id}`);
      toast.success('تم حذف البوست');
      onRefresh();
    } catch { toast.error('فشل الحذف'); } finally { setDeleting(false); }
  };

  const timeAgo = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 1) return 'الآن';
    if (diff < 60) return `منذ ${diff} دقيقة`;
    if (diff < 1440) return `منذ ${Math.floor(diff / 60)} ساعة`;
    return `منذ ${Math.floor(diff / 1440)} يوم`;
  };

  return (
    <div className="card animate-slideUp" style={{ borderRight: '3px solid var(--primary)' }}>
      {editing ? (
        <form onSubmit={handleEdit}>
          <div className="form-group">
            <input className="form-input" value={editForm.title}
              onChange={e => setEditForm({ ...editForm, title: e.target.value })} placeholder="العنوان" />
          </div>
          <div className="form-group">
            <textarea className="form-textarea" value={editForm.details}
              onChange={e => setEditForm({ ...editForm, details: e.target.value })} placeholder="التفاصيل" />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="submit" className="btn btn-primary btn-sm">حفظ</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>إلغاء</button>
          </div>
        </form>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <h3 style={{ fontWeight: 700, fontSize: '1rem', flex: 1 }}>{localPost.title}</h3>
            {isAdmin && (
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginRight: '8px' }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)} style={{ padding: '4px 8px' }}>
                  <Edit3 size={14} />
                </button>
                <button className="btn btn-danger btn-sm" onClick={handleDelete} disabled={deleting} style={{ padding: '4px 8px' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            )}
          </div>

          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: '16px' }}>
            {localPost.details}
          </p>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className={`btn btn-sm ${userReaction === 'like' ? 'btn-success' : 'btn-secondary'}`}
                onClick={() => handleReact('like')} disabled={reacting}
                style={{ padding: '6px 12px' }}
              >
                <ThumbsUp size={14} />
                {localPost.likes?.length || 0}
              </button>
              <button
                className={`btn btn-sm ${userReaction === 'dislike' ? 'btn-danger' : 'btn-secondary'}`}
                onClick={() => handleReact('dislike')} disabled={reacting}
                style={{ padding: '6px 12px' }}
              >
                <ThumbsDown size={14} />
                {localPost.dislikes?.length || 0}
              </button>
            </div>

            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Calendar size={11} /> {timeAgo(post.createdAt)}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default PostCard;
